import { useIconProviderContext } from './IconProvider';
import { useTheme } from '../ThemeProvider';

import React, { useId } from 'react';
import { type ColorValue, I18nManager } from 'react-native';
import Svg, { LinearGradient, Stop } from 'react-native-svg';

export type IconSize = 'md' | 'sm' | 'xs' | 'xxs';

export type IconPaths = Record<string, React.JSX.Element>;

export interface IconProps {
    children?: React.ReactNode;
    /**
     * Tint color
     */
    color?: ColorValue;
    /**
     * Array of colors to use for the gradient.
     */
    colors?: ColorValue[];
    /**
     * The interior color of the icon. You'll most likely want to use this prop to set the color of the icon.
     */
    fill?: ColorValue;
    /**
     * The edge color of the icon.
     */
    stroke?: ColorValue;
    /**
     * Object containing the size as the key and React.JSX path elements as the values.
     */
    paths?: IconPaths;
    /**
     * The size of the icon. Supported sizes are `xs`, `sm`, and `md`.
     */
    size?: IconSize;
    /**
     * Provides a human-readable title for the element that contains it.
     * https://www.w3.org/TR/SVG-access/#Equivalent
     */
    title?: string;
    /**
     * Test ID for testing purposes
     */
    testID?: string;
    // /**
    //  * Whether to use the `direction` property of the current theme to provide
    //  * an alternative version of this icon for RTL directions. In IFL 7, the
    //  * default will change to `true`. If you don't want icons to automatically
    //  * swap you can explicitly set `useDirection={false}` now.
    //  * @default false
    //  */
    // useDirection?: boolean;

    /**
     * The variant of the icon.
     * @default `filled`
     */
    variant?: 'filled' | 'stroke';

    /**
     * Whether to hide the icon from screen readers.
     */
    'aria-hidden'?: boolean;
}

type FixedIconOmitKeys = 'paths' | 'children';
export type FixedIconProps = Omit<IconProps, FixedIconOmitKeys>;

const viewboxSizes = {
    xxs: 16,
    xs: 18,
    sm: 20,
    md: 24,
};

// Source units are in rem, hard coding the px values by taking 1rem=16px from the web.
export const svgSizes = {
    xxs: 16,
    xs: 18,
    sm: 20,
    md: 24,
};

export function Icon(props: IconProps): React.JSX.Element {
    const theme = useTheme();
    const {
        children,
        color,
        colors,
        fill = 'currentColor',
        stroke,
        paths,
        size = 'md',
        variant,
        title,
        testID,
        ...remainingProps
    } = props;

    /**
     * a mapping from colour names to aurora colour values
     */
    const COLORS: Record<string, string> = {
        foregroundWeak: theme.colors.foregroundWeak,
    };

    /**
     * front end code is specified in the data model!!!
     * we need to intercept and correct it manually here
     *
     * fallback to the name if we aren't expecting the color value
     */
    function getColor(color: ColorValue): ColorValue {
        if (typeof color === 'string') {
            return COLORS[color] ?? color;
        }

        return color;
    }

    let viewBox: string;
    // TODO: paths with size should be removed in IFL 7.0
    if (paths && size in paths) {
        viewBox = `0 0 ${viewboxSizes[size]} ${viewboxSizes[size]}`;
    } else {
        viewBox = '0 0 24 24';
    }

    const { variant: variantFromContext } = useIconProviderContext();
    const a11yShouldHide = props['aria-hidden'] || !title; // If a title isn't present, then assume we are hiding from a screen reader
    const a11yLabel = !a11yShouldHide ? title : undefined;
    const computedVariant = variant ?? variantFromContext ?? 'filled';

    // Direction was only used for handling RTL. Assuming we won't experiment on
    // RTL locales, we don't need handle the direction variable here. Anyways,
    // leaving the const here for posterity.
    const direction = I18nManager.isRTL ? 'rtl' : '';

    let pathKey: string;
    if (paths && (size in paths || 'sm' in paths || 'md' in paths)) {
        pathKey = `${size}${direction}`;
    } else {
        pathKey = `${computedVariant}${direction}`;
    }

    const gradientId = `Icon-gradient-${useId()}`;

    const backgroundGradient = Array.isArray(colors) ? (
        <LinearGradient
            id={gradientId}
            x1={'0'}
            x2={'24'}
            y1={'0'}
            y2={'24'}
            gradientUnits="userSpaceOnUse"
        >
            {colors.map((stopColor, index, array) => (
                <Stop
                    key={index}
                    offset={index / (array.length - 1)}
                    stopColor={getColor(stopColor)}
                />
            ))}
        </LinearGradient>
    ) : (
        <></>
    );

    let path = paths ? paths[pathKey] : null;

    if (path != null && Array.isArray(colors)) {
        // Apply gradient to path if colors array is provided
        path = React.cloneElement(path, {
            fill: `url(#${gradientId})`,
        });
    }

    return (
        <Svg
            focusable={false}
            color={color && getColor(color)}
            role={a11yShouldHide ? "presentation" : "img"}
            fill={fill}
            stroke={stroke}
            testID={testID}
            height={svgSizes[size]}
            width={svgSizes[size]}
            viewBox={viewBox}
            accessible={!a11yShouldHide}
            accessibilityRole="image"
            accessibilityLabel={a11yLabel}
            title={a11yLabel}
            {...remainingProps}
            aria-hidden={a11yShouldHide ? true : undefined}
        >
            {colors !== undefined && backgroundGradient}
            {path ? path : children}
        </Svg>
    );
}
